/**
 * Connecting a broker.
 *
 * **The form is rendered from the API's description of it, not from knowledge of
 * any broker.** `/v1/broker/challenge` returns the fields, their types, their
 * hints and which of them are secrets; this file renders whatever it is given.
 * Adding CQG or Tradovate is an adapter change and nothing here moves — which is
 * the entire justification for the adapter interface, and it only holds if the
 * client never special-cases a broker. It does not.
 *
 * **The licensing notice is above the form, not behind a disclosure.** Connecting
 * can take over the user's single market data session and, done carelessly,
 * reclassify them as a professional subscriber at roughly fifteen times the
 * monthly fee — billed to them by their broker, not by us. A user deciding
 * whether to connect needs that in front of them at the moment of the decision.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  api, ApiError, fmtSince,
  type BrokerChallenge, type BrokerConnectionRow,
} from '../lib/api.js';
import { Link } from '../lib/router.js';

export function BrokerScreen(): JSX.Element {
  const [challenge, setChallenge] = useState<BrokerChallenge | null>(null);
  const [connections, setConnections] = useState<BrokerConnectionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Held HERE rather than inside `ConnectionList`.
   *
   * Disconnecting the last broker empties the list, which unmounts the
   * component — taking "Disconnected. Your journal is unchanged." with it. The
   * user clicks a destructive, confirmed action and gets no acknowledgement at
   * all, which reads as a failure. The screen outlives the list, so the screen
   * owns the message.
   */
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, list] = await Promise.all([api.brokerChallenge(), api.brokerConnections()]);
      setChallenge(c);
      setConnections(list.connections);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load broker settings.');
      setConnections([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Broker</h2>
        <Link to="/settings" className="btn btn-quiet compact">Back to settings</Link>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}
      {message && <div className="form-ok" role="status">{message}</div>}

      {connections === null ? (
        <p className="dim">Loading…</p>
      ) : connections.length > 0 ? (
        <ConnectionList connections={connections} onChange={load} onMessage={setMessage} />
      ) : null}

      {challenge && (
        <ConnectForm challenge={challenge} onConnected={load} onMessage={setMessage} />
      )}
    </div>
  );
}

function ConnectionList({
  connections, onChange, onMessage,
}: {
  connections: BrokerConnectionRow[];
  onChange(): void;
  onMessage(m: string | null): void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  async function test(id: string): Promise<void> {
    setBusy(id); setError(null); onMessage(null);
    try {
      const res = await api.brokerTest(id);
      if (res.ok) onMessage(res.reason || 'Connection is healthy.');
      else setError(res.reason);
      onChange();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not test the connection.');
    } finally {
      setBusy(null);
    }
  }

  async function disconnect(id: string): Promise<void> {
    setBusy(id); setError(null); onMessage(null);
    try {
      await api.brokerDisconnect(id);
      onMessage('Disconnected. Your journal is unchanged.');
      setConfirming(null);
      onChange();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not disconnect.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="settings-block">
      <h3 className="panel-h">CONNECTED · {connections.length}</h3>

      {error && <div className="form-error" role="alert">{error}</div>}

      <ul className="device-list">
        {connections.map((c) => (
          <li key={c.id} className="device-row">
            <div className="device-main">
              <span className="device-label">
                {c.displayName}
                <span className={`chip ${c.status === 'active' ? 'chip-ok' : 'chip-breached'}`}>
                  {c.status}
                </span>
              </span>
              <span className="device-meta mono">
                {c.connectionMode} mode
                {c.gateway ? ` · ${c.gateway}` : ''}
                {' · '}
                {c.lastSyncAt ? `synced ${fmtSince(c.lastSyncAt)}` : 'never synced'}
              </span>
              <span className="device-meta">
                {c.holdsCredential
                  ? 'AEGIS holds an encrypted copy of your login.'
                  : 'AEGIS holds no credential for this connection.'}
              </span>
              {c.lastError && <span className="device-meta field-hint-warn">{c.lastError}</span>}
            </div>

            <div className="settings-links">
              <button
                className="btn btn-quiet compact"
                onClick={() => void test(c.id)}
                disabled={busy !== null}
              >
                {busy === c.id ? 'Testing…' : 'Test'}
              </button>
              {confirming === c.id ? (
                <>
                  <button
                    className="btn btn-danger compact"
                    onClick={() => void disconnect(c.id)}
                    disabled={busy !== null}
                  >
                    Confirm
                  </button>
                  <button className="btn btn-quiet compact" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="btn btn-danger compact" onClick={() => setConfirming(c.id)}>
                  Disconnect
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {confirming && (
        <p className="field-hint">
          Disconnecting closes the upstream session and deletes the stored credential.
          Your journal, notes and analytics are kept — they are your record of your own
          trading, and disconnecting a broker is not a request to delete history.
        </p>
      )}
    </section>
  );
}

function ConnectForm({
  challenge, onConnected, onMessage,
}: {
  challenge: BrokerChallenge;
  onConnected(): void;
  onMessage(m: string | null): void;
}): JSX.Element {
  const [values, setValues] = useState<Record<string, string>>(() => {
    // Selects start on their first option rather than empty, so a user who
    // ignores the field submits something valid instead of a validation error.
    const initial: Record<string, string> = {};
    for (const f of challenge.fields ?? []) {
      if (f.type === 'select' && f.options?.length) initial[f.name] = f.options[0]!.value;
    }
    return initial;
  });
  const [nickname, setNickname] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const blocked = !challenge.canStoreCredentials && (challenge.secretFields?.length ?? 0) > 0;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending || blocked) return;
    setError(null); setResult(null); onMessage(null); setPending(true);

    try {
      const res = await api.brokerConnect(values, nickname.trim() || undefined);
      setResult(
        res.accountsImported > 0
          ? `Connected. Imported ${res.accountsImported} account${res.accountsImported === 1 ? '' : 's'}.`
          : 'Connected, but no accounts came back. Use Test to check the connection.',
      );
      // Passwords do not linger in component state after they have been used.
      setValues({});
      setNickname('');
      onConnected();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not connect.');
    } finally {
      setPending(false);
    }
  }

  if (challenge.kind === 'none') {
    return (
      <section className="settings-block">
        <h3 className="panel-h">CONNECT</h3>
        <p className="panel-body">
          {challenge.brokerName} needs no credential on this deployment.
        </p>
      </section>
    );
  }

  return (
    <section className="settings-block">
      <h3 className="panel-h">CONNECT {challenge.brokerName.toUpperCase()}</h3>

      {/* Never collapsed, never behind a "learn more". */}
      {challenge.notice && (
        <div className="notice-bar notice-block" role="status">{challenge.notice}</div>
      )}

      {challenge.recommendedMode === 'plugin' && (
        <p className="field-hint">
          On Windows with R|Trader Pro running, the plug-in mode is better than this
          form: AEGIS rides the session you already have, never receives your password,
          and adds no market data session. Use it where you can — the safest way to hold
          a secret is not to.
        </p>
      )}

      {blocked && (
        <div className="form-error" role="alert">
          This deployment has no <code className="mono">BROKER_ENCRYPTION_KEY</code>, so it
          cannot store a broker credential. It refuses rather than storing your password
          unencrypted. Set the key on the server and reload.
        </div>
      )}

      <form onSubmit={onSubmit} className="settings-form" noValidate>
        {(challenge.fields ?? []).map((f) => (
          <label className="field" key={f.name}>
            <span className="field-label">
              {f.label}{!f.required && <span className="dim"> (optional)</span>}
            </span>

            {f.type === 'select' ? (
              <select
                className="input select-wide"
                value={values[f.name] ?? ''}
                disabled={blocked}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              >
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                type={f.type === 'password' ? 'password' : 'text'}
                // Password managers should not offer to save a *broker* password
                // as though it were the AEGIS one.
                autoComplete="off"
                placeholder={f.placeholder}
                required={f.required}
                disabled={blocked}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              />
            )}

            {f.hint && <span className="field-hint">{f.hint}</span>}
          </label>
        ))}

        <label className="field">
          <span className="field-label">Name this connection <span className="dim">(optional)</span></span>
          <input
            className="input" type="text" maxLength={64} disabled={blocked}
            placeholder="Main account"
            value={nickname} onChange={(e) => setNickname(e.target.value)}
          />
        </label>

        {error && <div className="form-error" role="alert">{error}</div>}
        {result && <div className="form-ok" role="status">{result}</div>}

        <button className="btn btn-primary" type="submit" disabled={pending || blocked}>
          {pending ? 'Verifying…' : 'Connect'}
        </button>

        <p className="field-hint">
          Your credentials are checked against the broker before anything is stored, so a
          typo fails here rather than becoming a connection that silently syncs nothing.
          They are then encrypted with a key held outside the database, and there is no
          endpoint anywhere that returns them.
        </p>
        <p className="field-hint">
          AEGIS reads. It cannot place, modify or cancel an order — the broker interface
          has no method for it, which is a structural property rather than a promise.
        </p>
      </form>
    </section>
  );
}
